import {Observable} from 'rxjs';
import i18next from 'i18next';
import moment from 'moment-timezone';

import {combineEpics} from 'web-app/util/redux-observable';
import {showDeleteDialogObservable, showDialogObservable, DialogChoice} from 'web-app/react/dialog';
import * as GlobalEventActions from 'web-app/react/global-event/actions';
import {track} from 'web-app/util/famlytics';
import {TrackingEvents} from 'web-app/util/famlytics/events';

import * as Api from './api';
import * as Actions from './actions';
import * as Constants from './constants';
import type * as Types from './types';
import * as Helpers from './helpers';

const fetchAllForChild = action$ =>
    action$.ofType(Actions.fetchAllForChild.type).mergeMap(({payload}) =>
        Api.fetchAllForChild(payload.childId)
            .map(reports => Actions.fetchAllForChildSuccess.action({reports, childId: payload.childId}))
            .catch(e => Observable.of(Actions.fetchAllForChildFailed.action(e))),
    );

const fetchWithIds = action$ =>
    action$.ofType(Actions.fetchWithIds.type).mergeMap(({payload}) =>
        Api.fetchWithIds(payload.reportIds)
            .map(reports => Actions.fetchWithIdsSuccess.action({reports}))
            .catch(e => Observable.of(Actions.fetchWithIdsFailed.action(e))),
    );

const prepareReportForAPI = <Report extends Types.ReportForApiCreate | Types.ReportForApiUpdate>(
    report: Report,
): Report => ({
    ...report,
    bodyMapPoints: report.bodyMapPoints.map(_ => ({
        // Filter out `imageLabel` before report is sent to API otherwise backend will throw an error
        // `imageLabel` is only used for tracking
        fileId: _.fileId,
        radius: _.radius,
        x: _.x,
        y: _.y,
    })),
});

const create = action$ =>
    action$.ofType(Actions.create.type).mergeMap(({payload}: ReturnType<typeof Actions.create.action>) =>
        showDialogObservable({
            title: '',
            description: i18next.t('childProfile.accidentIncidentReports.createConfirmation'),
        }).mergeMap(selection => {
            if (selection === DialogChoice.OK) {
                trackUpsert(payload, true);
                return Api.createAccidentIncidentReport(prepareReportForAPI(payload.report))
                    .flatMap(report => [
                        Actions.createSuccess.action({report}),
                        GlobalEventActions.updateSuccess.action(
                            i18next.t('childProfile.accidentIncidentReports.created'),
                        ),
                    ])
                    .catch(e => Observable.of(Actions.createFailed.action(e)));
            } else {
                return Observable.of(Actions.createAbort.action());
            }
        }),
    );

const update = action$ =>
    action$.ofType(Actions.update.type).mergeMap(({payload}: ReturnType<typeof Actions.update.action>) =>
        showDialogObservable({
            title: '',
            description: i18next.t('childProfile.accidentIncidentReports.updateConfirmation'),
        }).mergeMap(selection => {
            if (selection === DialogChoice.OK) {
                trackUpsert(payload, false);
                payload.completeTestChildAction?.();
                return Api.updateAccidentIncidentReport(prepareReportForAPI(payload.report))
                    .flatMap(report => [
                        Actions.updateSuccess.action({report}),
                        GlobalEventActions.updateSuccess.action(
                            i18next.t('childProfile.accidentIncidentReports.updated'),
                        ),
                    ])
                    .catch(e => Observable.of(Actions.updateFailed.action(e)));
            } else {
                return Observable.of(Actions.updateAbort.action());
            }
        }),
    );

const trackUpsert = (payload: {report: Types.ReportForApiCreate | Types.ReportForApiUpdate}, creating) => {
    track(TrackingEvents.ACCIDENT_INCIDENT_REPORTS_REPORT_CREATED, {
        numberOfCharactersNatureOfAccident: payload.report?.description?.length ?? 0,
        numberOfCharactersHowWereParentsNotified: payload.report?.parentsNotified?.length ?? 0,
        numberOfCharactersOtherNotes: payload.report?.note?.length ?? 0,
        kind: payload.report?.kind ?? '',
        dateTimeForAccident: moment(`${payload.report?.date} ${payload.report?.time}`).toISOString(),
        nubmerOfFiles: payload.report?.files?.length ?? 0,
        new: creating,
        ...Helpers.bodyMapPointsToTrackingData(payload.report.bodyMapPoints),
    });

    if (payload.report?.kind === Constants.KIND.Accident) {
        track(TrackingEvents.SAFEGUARDING);
    }
};

const acknowledge = action$ =>
    action$.ofType(Actions.acknowledge.type).mergeMap(({payload}) =>
        Api.acknowledge(payload.reportId)
            .map(data => Actions.acknowledgeSuccess.action(data))
            .catch(e => Observable.of(Actions.acknowledgeFailed.action(e, payload.reportId))),
    );

const deleteReport = action$ =>
    action$.ofType(Actions.deleteReport.type).mergeMap(({payload}) =>
        showDeleteDialogObservable({
            title: '',
            description: i18next.t('childProfile.accidentIncidentReports.deleteConfirmation'),
        }).mergeMap(selection => {
            if (selection === DialogChoice.OK) {
                return Api.deleteReport(payload.reportId)
                    .flatMap(() => [
                        Actions.deleteReportSuccess.action({reportId: payload.reportId, childId: payload.childId}),
                        GlobalEventActions.updateSuccess.action(
                            i18next.t('childProfile.accidentIncidentReports.deleted'),
                        ),
                    ])
                    .catch(e => Observable.of(Actions.deleteReportFailed.action(e)));
            } else {
                return Observable.of(Actions.deleteReportAborted.action());
            }
        }),
    );

export default combineEpics(fetchAllForChild, fetchWithIds, create, update, acknowledge, deleteReport);
